import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Spin,
  Table,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import {
  AuditOutlined,
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  updateInvoice,
  getCustomers,
  getMyProfile,
} from "../../../network/network";

import { AppContext } from "../../../context/AppContextProvider";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import {
  AddonName,
  SaleInvoiceStatus,
} from "../../../configs/constants";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import TaxCalculator from "../../../components/TaxCalculator";
import moment from "moment";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

import QuantityStepper from "../../../components/QuantityStepper";
import TagOptionsModal from "../POS/components/TagOptionsModal";
const EditDraftInvoices = () => {
  const { authUser, appConfig, roundOff } = useContext(AppContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productsData, setProductsData] = useState([]);
  const [cart, setCart] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [customerData, setCustomerData] = useState([]);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedProductForTags, setSelectedProductForTags] = useState(null);
  const [selectedTagsForModal, setSelectedTagsForModal] = useState([]);
  let { salesInvoiceId } = useParams();
  const { t } = useTranslation();
  const [invoiceForm] = Form.useForm();
  const { state } = useLocation();
  let navigate = useNavigate();

  const is_stock_management_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.Stock,
  );

  const getAvailableTags = useCallback((product) => {
    if (
      Array.isArray(product?.available_tags) &&
      product.available_tags.length > 0
    ) {
      return product.available_tags;
    }

    if (Array.isArray(product?.tags)) {
      return product.tags;
    }

    return [];
  }, []);

  const normalizeTagIds = useCallback((values) => {
    if (!Array.isArray(values)) return [];
    return values
      .map((value) => (value && typeof value === "object" ? value.id : value))
      .filter((value) => value !== undefined && value !== null);
  }, []);

  const hasTagId = useCallback(
    (ids = [], id) => ids.some((tagId) => String(tagId) === String(id)),
    [],
  );

  const tagIdsFrom = useCallback(
    (tags = []) =>
      normalizeTagIds(
        tags.map((tag) => (tag && tag.id !== undefined ? tag.id : tag)),
      ),
    [normalizeTagIds],
  );

  const makeVariantKey = useCallback(
    (productId, tagIds = []) =>
      `${productId}_${
        normalizeTagIds(tagIds).map(String).sort().join(",") || "no_tags"
      }`,
    [normalizeTagIds],
  );

  const resetTagModal = () => {
    setTagModalVisible(false);
    setModalMode("add");
    setSelectedProductForTags(null);
    setSelectedTagsForModal([]);
  };

  const buildInvoiceItem = (productBase, tagIds = []) => {
    const normalizedTagIds = normalizeTagIds(tagIds);
    const availableTags = getAvailableTags(productBase);
    const selectedTags = availableTags.filter((tag) =>
      hasTagId(normalizedTagIds, tag.id),
    );

    return {
      ...productBase,
      product_id: productBase.product_id || productBase.id,
      available_tags: availableTags,
      tags: selectedTags,
      selectedTagIds: normalizedTagIds,
      cart_key:
        productBase.cart_key ||
        makeVariantKey(
          productBase.product_id || productBase.id,
          normalizedTagIds,
        ),
    };
  };

  useEffect(() => {
    state ? setData(state) : navigate("/sales-invoice/" + salesInvoiceId);
  }, [navigate, salesInvoiceId, state]);

  useEffect(() => {
    setLoading(true);
    getCustomers({
      per_page: -1,
    })
      .then((response) => {
        let data = response.data || response.response.data;
        if (data.success === true) {
          setCustomerData(response.data.data.customers);
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoading(false);
      });
  }, [t]);

  useEffect(() => {
    let categories = [
      { id: 0, name: t("all"), name_ar: t("all", { lng: "ar" }) },
    ];
    let products = [];
    getMyProfile()
      .then((response) => {
        let data = response.data || response.response.data;
        if (data.success === true) {
          data.data.user.categories.forEach((category) => {
            if (category.products.length > 0) {
              categories.push({
                id: category.id,
                name: category.name,
                name_ar: category.name_ar,
              });
              products = [...products, ...category.products];
            }
          });
          setProductsData(products);
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  useEffect(() => {
    invoiceForm.setFieldsValue({
      id: data.id,
      customer_id: data?.customer?.id,
      invoice_due_date: moment(data?.invoice_due_date),
    });
  }, [data, invoiceForm]);

  useEffect(() => {
    if (data && data.items && data.items.length > 0) {
      const mergedA = data.items.map((itemA) => {
        const matchingItem = productsData.find(
          (itemB) => Number(itemB.id) === Number(itemA.product_id),
        );
        const availableTags = matchingItem
          ? getAvailableTags(matchingItem)
          : [];
        const rawTagIds = Array.isArray(itemA.tags)
          ? tagIdsFrom(itemA.tags)
          : [];
        const selectedTags = Array.isArray(itemA.tags)
          ? typeof itemA.tags[0] === "object" && itemA.tags[0]?.id !== undefined
            ? itemA.tags
            : availableTags.filter((tag) => hasTagId(rawTagIds, tag.id))
          : [];
        const selectedTagIds = rawTagIds;

        return {
          ...itemA,
          is_taxable: matchingItem?.is_taxable ?? itemA.is_taxable,
          is_stockable: matchingItem?.is_stockable ?? itemA.is_stockable,
          stock: matchingItem?.stock ?? itemA.stock,
          available_tags: availableTags,
          tags: selectedTags,
          selectedTagIds,
          cart_key: itemA.id
            ? `line_${itemA.id}`
            : makeVariantKey(itemA.product_id, selectedTagIds),
        };
      });

      setCart(JSON.parse(JSON.stringify(mergedA)));
    }
  }, [
    data,
    getAvailableTags,
    hasTagId,
    makeVariantKey,
    productsData,
    tagIdsFrom,
  ]);

  useEffect(() => {
    let tax = 0;
    let total = 0;

    cart.forEach((product) => {
      const productSubTotal = product.price * product.quantity;
      let productTax = 0;

      if ((product.is_taxable || product.tax > 0) && product.taxes?.length) {
        // Calculate each tax separately
        product.taxes.forEach((tax) => {
          productTax += productSubTotal * (Number(tax.rate) / 100);
        });
      }

      tax += productTax;
      total += productSubTotal + productTax;
    });

    setTotalTax(roundOff(tax));
    setTotalAmount(roundOff(total));
  }, [cart, roundOff]);

  const getTotalQuantityForProduct = (items, productId) =>
    items
      .filter((item) => Number(item.product_id) === Number(productId))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  const cartReducer = (product, action) => {
    let updatedCart = JSON.parse(JSON.stringify(cart));
    const productId = product.product_id || product.id;
    const productCartKey =
      product.cart_key ||
      makeVariantKey(productId, product.selectedTagIds || product.tags || []);
    const indexFound = updatedCart.findIndex(
      (cartItem) => cartItem.cart_key === productCartKey,
    );

    if (action === "add") {
      const invoiceItem = buildInvoiceItem(
        product,
        product.selectedTagIds || [],
      );
      const totalQtyForProduct = getTotalQuantityForProduct(
        updatedCart,
        productId,
      );

      if (indexFound === -1) {
        if (
          is_stock_management_active &&
          invoiceItem.is_stockable &&
          totalQtyForProduct >= Number(invoiceItem.stock || 0)
        ) {
          showAppError(t("insufficient_quantity"));
        } else {
          updatedCart.push({ ...invoiceItem, quantity: 1 });
        }
      } else if (
        is_stock_management_active &&
        updatedCart[indexFound].is_stockable &&
        totalQtyForProduct >= Number(updatedCart[indexFound].stock || 0)
      ) {
        showAppError(t("insufficient_quantity"));
      } else {
        updatedCart[indexFound].quantity++;
      }
    } else if (indexFound !== -1 && action === "inc") {
      const totalQtyForProduct = getTotalQuantityForProduct(
        updatedCart,
        productId,
      );
      if (
        is_stock_management_active &&
        updatedCart[indexFound].is_stockable &&
        totalQtyForProduct >= Number(updatedCart[indexFound].stock || 0)
      ) {
        showAppError(t("insufficient_quantity"));
      } else {
        updatedCart[indexFound].quantity++;
      }
    } else if (indexFound !== -1 && action === "dec") {
      const newQty = Number(updatedCart[indexFound].quantity || 0) - 1;
      if (newQty <= 0) {
        updatedCart.splice(indexFound, 1);
      } else {
        updatedCart[indexFound].quantity = newQty;
      }
    } else if (indexFound !== -1 && action === "remove") {
      updatedCart.splice(indexFound, 1);
    } else if (indexFound !== -1 && action === "update_options") {
      const newTagIds = normalizeTagIds(product.selectedTagIds);
      const currentItem = updatedCart[indexFound];
      const selectedTags = (currentItem.available_tags || []).filter((tag) =>
        hasTagId(newTagIds, tag.id),
      );
      const newVariantKey = currentItem.id
        ? currentItem.cart_key
        : makeVariantKey(currentItem.product_id, newTagIds);

      const duplicateIndex = currentItem.id
        ? -1
        : updatedCart.findIndex(
            (item, index) =>
              item.cart_key === newVariantKey && index !== indexFound,
          );

      if (duplicateIndex !== -1) {
        updatedCart[duplicateIndex].quantity =
          Number(updatedCart[duplicateIndex].quantity || 0) +
          Number(currentItem.quantity || 0);
        updatedCart.splice(indexFound, 1);
      } else {
        updatedCart[indexFound] = {
          ...currentItem,
          tags: selectedTags,
          selectedTagIds: newTagIds,
          cart_key: newVariantKey,
        };
      }
    }
    setCart(updatedCart);
  };

  const handleProductSelect = (value) => {
    const product = productsData.find((product) => product.id === value);
    if (!product) return;

    const productForCart = buildInvoiceItem(product, []);
    const availableTags = getAvailableTags(productForCart);

    if (availableTags.length > 0) {
      setModalMode("add");
      setSelectedProductForTags(productForCart);
      setSelectedTagsForModal([]);
      setTagModalVisible(true);
    } else {
      cartReducer(productForCart, "add");
    }

    invoiceForm.setFieldsValue({
      product: "",
    });
  };

  const openEditTagModalForCartItem = (cartItem) => {
    setModalMode("edit");
    setSelectedProductForTags(cartItem);
    setSelectedTagsForModal(
      tagIdsFrom(cartItem.tags || cartItem.selectedTagIds || []),
    );
    setTagModalVisible(true);
  };

  const handleAddToCartFromTagModal = () => {
    if (!selectedProductForTags) return;

    if (modalMode === "edit") {
      cartReducer(
        {
          cart_key: selectedProductForTags.cart_key,
          selectedTagIds: selectedTagsForModal,
        },
        "update_options",
      );
    } else {
      cartReducer(
        buildInvoiceItem(selectedProductForTags, selectedTagsForModal),
        "add",
      );
    }

    resetTagModal();
  };

  const SubtotalDisplay = ({ record }) => {
    let subTotal = record.price * record.quantity;

    if (record.is_taxable && record.taxes) {
      const totalTaxRate = record.taxes.reduce(
        (sum, tax) => sum + Number(tax.rate),
        0,
      );
      subTotal += subTotal * (totalTaxRate / 100);
    }

    return roundOff(subTotal);
  };

  const tableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("image"),
      width: 85,
      render: (record) => {
        return (
          <>
            <img
              alt={record.name_en}
              src={record.image ? record.image : "/images/image-not-found.png"}
              className="app-table-thumbnail"
            />
          </>
        );
      },
    },
    {
      title: t("product"),
      render: (record) => {
        return (
          <>{appConfig.direction === "ltr" ? record.name_en : record.name}</>
        );
      },
    },
    {
      title: t("options"),
      align: "center",
      render: (record) =>
        (record.available_tags || []).length > 0 ? (
          <div>
            <Button
              icon={<TagsOutlined />}
              type="link"
              onClick={() => openEditTagModalForCartItem(record)}
            >
              {record.tags && record.tags.length > 0
                ? `${record.tags.length} ${t("selected")}`
                : t("select_options")}
            </Button>
            {record.tags && record.tags.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {record.tags.map((tag) => (
                  <span
                    key={tag.id ?? tag}
                    style={{
                      display: "inline-block",
                      marginRight: 6,
                      marginBottom: 4,
                      padding: "2px 8px",
                      background: "var(--color-bg)",
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                  >
                    {appConfig.direction === "rtl"
                      ? tag.name_ar || tag.name
                      : tag.name || tag.name_en}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: t("unit_price"),
      render: (record) => {
        return (
          <>
            <CurrencyFormatter value={record.price} />
          </>
        );
      },
    },
    {
      title: t("quantity"),
      render: (record) => {
        return (
          <>
            <QuantityStepper
              value={record.quantity}
              min={1}
              decreaseDisabled={record.quantity <= 1}
              increaseDisabled={
                record.is_stockable ? record.stock <= record.quantity : false
              }
              onDecrease={() => cartReducer(record, "dec")}
              onIncrease={() =>
                record.is_stockable
                  ? record.stock > record.quantity && cartReducer(record, "inc")
                  : cartReducer(record, "inc")
              }
            />
          </>
        );
      },
    },
    {
      title: t("price_including_tax"),
      render: (record) => {
        return (
          <>
            <CurrencyFormatter value={SubtotalDisplay({ record })} />
          </>
        );
      },
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (record) => {
        return (
          <Space>
            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => cartReducer(record, "remove")}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "sales-invoice",
      breadcrumbName: t("sales_invoice"),
      icon: <AuditOutlined />,
    },
    {
      path: "edit-sales-invoice",
      breadcrumbName: t("invoice_edit"),
      icon: <EditOutlined />,
    },
  ];

  const handleEdit = () => {
    invoiceForm
      .validateFields()
      .then((values) => {
        // Set Loading True
        setLoading(true);
        // Sending Request to API
        values.product = "";
        values.sale_invoice_status = SaleInvoiceStatus.Draft;
        values.items = JSON.stringify(
          cart.map((item) => ({
            id: item.product_id,
            qty: item.quantity,
            price: item.price,
            tags:
              item.tags && item.tags.length > 0
                ? tagIdsFrom(item.tags)
                : normalizeTagIds(item.selectedTagIds),
          })),
        );

        updateInvoice(values)
          .then((response) => {
            let data = response.data || response.response.data;
            if (data.success === true) {
              // Success
              NotificationWithIcon(
                "success",
                t("invoice_updated"),
                t("invoice_updated_successfully"),
              );
              navigate("/sales-invoice/" + salesInvoiceId);
            } else {
              handleApiError(response, t);
            }
          })
          .catch((error) => handleApiError(error, t))
          .finally(() => {
            // Set Loading False
            setLoading(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <div className="app-relative">
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          title={t("invoice_edit")}
          onBack={() => window.history.back()}
          ghost={false}
          className="app-p-0"
        />
      </Card>
      <Spin spinning={loading}>
        <Card
          style={{
            direction: appConfig.direction
              ? appConfig.direction === "ltr"
                ? "ltr"
                : "rtl"
              : "ltr",
          }}
        >
          {data && (
            <Form
              name="edit-invoice-form"
              layout="vertical"
              onFinish={handleEdit}
              className="app-w-full"
              form={invoiceForm}
            >
              <Form.Item name="id" hidden={true}>
                <input />
              </Form.Item>
              <Row gutter={[20, 20]}>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <Form.Item
                    name="customer_id"
                    label={t("customer_name")}
                    rules={[
                      {
                        required: true,
                        message: t("validate_customer_name_required"),
                      },
                    ]}
                    hasFeedback
                    className="app-w-full"
                  >
                    <Select
                      showSearch
                      allowClear
                      options={customerData.map((customer) => {
                        return {
                          label: customer.name_ar,
                          value: customer.id,
                        };
                      })}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <Form.Item
                    name="invoice_due_date"
                    label={t("due_date")}
                    rules={[
                      {
                        required: true,
                        message: t("validate_date_required"),
                      },
                    ]}
                    hasFeedback
                  >
                    <DatePicker
                      disabledDate={(current) => {
                        return current && current < moment().add(-1, "day");
                      }}
                      format="YYYY-MM-DD"
                      allowClear
                      className="app-w-full"
                    />
                  </Form.Item>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <Form.Item
                    name="product"
                    label=<b>{t("product_add")}</b>
                    rules={[
                      cart.length < 1 && {
                        required: true,
                        message: t("validate_product_name_required"),
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      allowClear
                      options={productsData.map((product) => {
                        return {
                          disabled: is_stock_management_active
                            ? product.is_stockable
                              ? product.stock > 0
                                ? false
                                : true
                              : false
                            : false,
                          label:
                            appConfig.direction === "ltr"
                              ? `${product.name_en} (${product.category})`
                              : `${product.name} (${product.category_ar})`,
                          value: product.id,
                        };
                      })}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      onChange={handleProductSelect}
                    />
                  </Form.Item>
                </Col>
                {cart.length > 0 && (
                  <Col
                    className="gutter-row"
                    xs={24}
                    sm={24}
                    md={24}
                    lg={24}
                    xl={24}
                    xxl={24}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        paddingBottom: "8px",
                      }}
                    >
                      {t("items")}
                    </div>
                    <Table
                      columns={tableColumns}
                      dataSource={cart}
                      loading={loading}
                      rowKey={(record) => record.cart_key}
                      pagination={false}
                    />
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        marginTop: "30px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px",
                          borderBottom: "1px solid var(--color-border)",
                        }}
                      >
                        {t("total_without_tax")}
                        <span>
                          <CurrencyFormatter value={totalAmount - totalTax} />
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px",
                          borderBottom: "1px solid var(--color-border)",
                        }}
                      >
                        <div>
                          {t("total_taxes")}
                          <Popover
                            content={<TaxCalculator data={cart} />}
                            trigger="click"
                          >
                            <InfoCircleOutlined className="app-info-icon" />
                          </Popover>
                        </div>
                        <span>
                          <CurrencyFormatter value={totalTax} />
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px",
                          color: "var(--color-primary-hover)",
                        }}
                      >
                        {authUser.is_vat_exempt
                          ? t("total")
                          : t("total_with_tax")}

                        <b>
                          <CurrencyFormatter value={totalAmount} />
                        </b>
                      </div>
                    </div>
                  </Col>
                )}
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                  xxl={24}
                >
                  <Form.Item className="app-text-center">
                    <Space>
                      <Button
                        type="default"
                        key="back"
                        onClick={() =>
                          navigate("/sales-invoice/" + salesInvoiceId)
                        }
                      >
                        {t("cancel")}
                      </Button>
                      <Button type="primary" htmlType="submit">
                        {t("submit")}
                      </Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          )}
        </Card>
      </Spin>

      <TagOptionsModal
        t={t}
        appConfig={appConfig}
        open={tagModalVisible}
        onClose={resetTagModal}
        product={selectedProductForTags}
        options={getAvailableTags(selectedProductForTags)}
        selected={selectedTagsForModal}
        onChangeSelected={setSelectedTagsForModal}
        onConfirm={handleAddToCartFromTagModal}
        title={t("select_options")}
        confirmText={selectedTagsForModal.length > 0 ? t("add") : t("skip")}
        cancelText={t("cancel")}
      />
    </div>
  );
};

export default EditDraftInvoices;
